// Neu (SPEC 03 §6.1) — EIN zustand-Speicher für Oberflächenzustand, der weder Serverdaten
// (TanStack Query) noch Adresse (URL) ist: Meldungen, Verfahren, Esc-Ebenen, Live-Stand,
// offene Blätter. Der Chat hat seinen eigenen Modulspeicher (screens/ki/chatStore.ts).
//
// Regel S1 (E13, Datensparsamkeit): in den Browserspeicher geht von hier NUR `hs.verfahren`.
// Nie ein Wortlaut, eine Lesefassung, eine Codeliste, eine Frage, ein Filterwert oder ein
// Suchbegriff (Prüfung: test/speicher.test.ts).
import { create } from "zustand";
import type { Rezept } from "../api/typen";
import { load, save } from "../lib/storage";

export interface Toast {
  id: number;
  type: "success" | "error" | "info" | "warn";
  msg: string;
  detail?: string;
  mono?: boolean;
  copy?: string;
  link?: { text: string; zu: string };
}

export type LiveModus = "live" | "abfrage" | "aus";

export interface ExportDialogZustand {
  quelle: "ansicht" | "abfrage" | "verfahren";
  rezept?: Rezept;
  abfrageId?: string;
}

/** Zusammenfassung der Hochladeschlange des Eingangs (ohne File-Objekt) — damit der
 *  Aufträge-Knopf der Kopfleiste sie sieht. Nur Speicher, nie Browserspeicher (S1). */
export interface HochladePosten {
  id: number;
  name: string;
  groesse: number;
  geladen: number;
  zustand: "wartet" | "laedt" | "fertig" | "fehler";
}

export interface ClientAuftraege {
  /** Hochladeschlange des Eingangs (verschwindet mit dem Bildschirm — der bricht beim Verlassen ab). */
  hochladen: { V: string; posten: HochladePosten[] } | null;
  /** Laufender Import aus dem Eingangsordner (die Anfrage läuft auch nach dem Verlassen weiter). */
  import: { V: string; dateien: number; seit: number } | null;
}

export interface AppZustand {
  toasts: Toast[];
  /** 5 s, Fehler 9 s, höchstens 4 gleichzeitig (der älteste weicht). */
  addToast(t: Omit<Toast, "id">): void;
  removeToast(id: number): void;

  /** Zuletzt benutztes Verfahren — gespeichert als `hs.verfahren`. */
  verfahren: string | null;
  setzeVerfahren(V: string): void;

  /** Esc-Stapel (SPEC 03 §8.4, §11.2): die oberste Ebene schließt zuerst. */
  ebenen: string[];
  oeffneEbene(id: string): void;
  schliesseEbene(id: string): void;

  live: { seq: number | null; modus: LiveModus };
  setzeLive(p: Partial<AppZustand["live"]>): void;

  filterSheet: boolean;
  setzeFilterSheet(v: boolean): void;

  verfahrenBlatt: boolean;
  setzeVerfahrenBlatt(v: boolean): void;

  exportDialog: ExportDialogZustand | null;
  setzeExportDialog(v: ExportDialogZustand | null): void;

  /**
   * Ergänzung (nicht in SPEC 03 §6.1, rein additiv): die aktuelle Breite des Belege-Docks in px
   * (0 = zu). Das Dock meldet sie, der Chat weicht ihr ≥ 1280 px aus (DESIGN §7).
   */
  dockBreite: number;
  setzeDockBreite(px: number): void;

  /** Ergänzung (Aufträge-Knopf): Arbeit, die nur der Browser kennt — Hochladen und Import. */
  auftraege: ClientAuftraege;
  setzeHochladen(v: ClientAuftraege["hochladen"]): void;
  setzeImport(v: ClientAuftraege["import"]): void;
}

const DAUER_MS = 5000;
const DAUER_FEHLER_MS = 9000;
const HOECHSTENS = 4;

let naechsteId = 1;
const uhren = new Map<number, ReturnType<typeof setTimeout>>();

export const useStore = create<AppZustand>((set, get) => ({
  toasts: [],
  addToast(t) {
    const id = naechsteId++;
    const toast: Toast = {
      ...t,
      id,
      // Fehler tragen immer eine kopierbare Meldung (webgis) — ohne Adressen, nur der Satz.
      copy: t.copy ?? (t.type === "error" ? [t.msg, t.detail].filter(Boolean).join("\n") : undefined),
    };
    const liste = [...get().toasts, toast];
    while (liste.length > HOECHSTENS) {
      const weg = liste.shift();
      if (weg) {
        const u = uhren.get(weg.id);
        if (u) clearTimeout(u);
        uhren.delete(weg.id);
      }
    }
    set({ toasts: liste });
    uhren.set(
      id,
      setTimeout(() => get().removeToast(id), t.type === "error" ? DAUER_FEHLER_MS : DAUER_MS),
    );
  },
  removeToast(id) {
    const u = uhren.get(id);
    if (u) clearTimeout(u);
    uhren.delete(id);
    set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }));
  },

  verfahren: load<string | null>("verfahren", null),
  setzeVerfahren(V) {
    if (get().verfahren === V) return;
    save("verfahren", V);
    set({ verfahren: V, live: { seq: null, modus: "aus" } });
  },

  ebenen: [],
  oeffneEbene(id) {
    set((s) => ({ ebenen: [...s.ebenen.filter((e) => e !== id), id] }));
  },
  schliesseEbene(id) {
    if (!get().ebenen.includes(id)) return;
    set((s) => ({ ebenen: s.ebenen.filter((e) => e !== id) }));
  },

  live: { seq: null, modus: "aus" },
  setzeLive(p) {
    set((s) => ({ live: { ...s.live, ...p } }));
  },

  filterSheet: false,
  setzeFilterSheet(v) {
    set({ filterSheet: v });
  },

  verfahrenBlatt: false,
  setzeVerfahrenBlatt(v) {
    set({ verfahrenBlatt: v });
  },

  exportDialog: null,
  setzeExportDialog(v) {
    set({ exportDialog: v });
  },

  dockBreite: 0,
  setzeDockBreite(px) {
    if (get().dockBreite !== px) set({ dockBreite: px });
  },

  auftraege: { hochladen: null, import: null },
  setzeHochladen(v) {
    set((s) => ({ auftraege: { ...s.auftraege, hochladen: v } }));
  },
  setzeImport(v) {
    set((s) => ({ auftraege: { ...s.auftraege, import: v } }));
  },
}));

/** Kurzform außerhalb von Komponenten: `toast({type:"success", msg:"Gespeichert"})`. */
export function toast(t: Omit<Toast, "id">): void {
  useStore.getState().addToast(t);
}

/* ------------------------------------------------------------------ Esc-Stapel
   Jede Ebene (Modal, modales Bodenblatt, Filterblatt, Chat, Dock, Übersichtsblatt) meldet sich
   beim Öffnen an und beim Schließen ab und hinterlegt, WAS beim Esc passiert. EIN keydown-Hörer
   in der Shell ruft `escOberste()` — nur die oberste Ebene schließt (DESIGN §6.3). */

const esc = new Map<string, () => void>();

/** Ebene anmelden; die Rückgabe meldet sie wieder ab (für useEffect). */
export function ebeneAnmelden(id: string, beiEsc: () => void): () => void {
  esc.set(id, beiEsc);
  useStore.getState().oeffneEbene(id);
  return () => {
    if (esc.get(id) === beiEsc) esc.delete(id);
    useStore.getState().schliesseEbene(id);
  };
}

/** true, wenn eine Ebene geschlossen wurde (dann schluckt der Aufrufer das Ereignis). */
export function escOberste(): boolean {
  const { ebenen } = useStore.getState();
  for (let i = ebenen.length - 1; i >= 0; i--) {
    const id = ebenen[i]!;
    const f = esc.get(id);
    if (f) {
      f();
      return true;
    }
  }
  return false;
}
